// ─── Panneau de filtres de la modale de rattachement (§6) ─────────
// ⚠️ CALQUE et non <Feuille> : ce panneau s'ouvre PAR-DESSUS la modale de
// rattachement, qui est déjà une Feuille. Deux <Modal> natives imbriquées
// figent l'écran — piège documenté dans le Core et dans COWORK.md.
// Conséquence assumée : pas de bouton ✓, donc chaque choix s'applique
// IMMÉDIATEMENT. La liste derrière est masquée par le calque, elle ne
// clignote donc pas.
// Six filtres qui se combinent en ET, plus un bouton Réinitialiser en pied.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import {
  CalqueChoix, ChoixPuces, SelecteurDuree, MOIS, ANNEE, TOUT, enMois, useUI, useT,
} from '@mop/core';
import { FILTRES_DEFAUT } from './services/selectionOperations';
import { emojiCompte } from './services/comptes';

export default function FeuilleFiltresRattachement({
  visible, filtres = {}, sensDefaut = null, comptes = [], reference = 0,
  onAppliquer, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState({ ...FILTRES_DEFAUT });

  useEffect(() => {
    if (visible) setF({ ...FILTRES_DEFAUT, sens: sensDefaut, ...filtres });
  }, [visible, filtres, sensDefaut]);

  const set = (k, v) => setF(x => { const n = { ...x, [k]: v }; onAppliquer?.(n); return n; });
  const basculerCompte = (id) => setF(x => {
    const l = x.comptes || [];
    const n = { ...x, comptes: l.includes(id) ? l.filter(c => c !== id) : [...l, id] };
    onAppliquer?.(n); return n;
  });

  // §6.1 — même sélecteur que la fiche de prévision (Cédric, 13/08) : compteur
  // ± et bouton d'unité tournant. Le filtre stocke des MOIS (ou 'tout'), mais
  // l'unité affichée est un choix d'écran : on la garde ici, sinon « 2 ans »
  // reviendrait en « 24 mois » à la réouverture.
  const [uniteVue, setUniteVue] = useState(MOIS);
  const dureeVue = f.periodeMois === TOUT
    ? TOUT
    : (uniteVue === ANNEE && Number(f.periodeMois) % 12 === 0
      ? { n: Number(f.periodeMois) / 12, unite: ANNEE }
      // ⚠️ Pas de `|| 1` : 0 est une VALEUR (« ce mois seul »), pas un vide.
      : { n: Math.max(0, Number(f.periodeMois) || 0), unite: MOIS });

  const changerDuree = (d) => {
    if (d !== TOUT) setUniteVue(d.unite);
    const mois = d === TOUT ? TOUT : enMois(d);
    setF(x => { const n = { ...x, periodeMois: mois }; onAppliquer?.(n); return n; });
  };

  // §6.2 — les bornes sont annoncées en euros : « ±10 % » seul ne dit pas
  // grand-chose quand on cherche une opération précise.
  const bornes = (tol) => {
    if (!reference || tol === 'tous') return '';
    if (tol === 'exact') return fmt(reference);
    const p = tol === '35' ? 0.35 : 0.10;
    return `${fmt(reference * (1 - p))} – ${fmt(reference * (1 + p))}`;
  };

  return (
    <CalqueChoix visible={visible} titre={t('finances.filtrerTrier')} emoji="🔎" onFermer={onFermer}>

      <Text style={s.lbl}>{t('finances.fPeriode')}</Text>
      {/* `avecTout` : au-delà du plafond on passe à « Tout », c'est-à-dire
          AUCUNE limite de période. On en ressort par [−]. */}
      <SelecteurDuree valeur={dureeVue} onChange={changerDuree} maxMois={120} avecTout min={0} />
      <Text style={s.aide}>{t('finances.horizon')}</Text>

      <Text style={s.lbl}>{t('finances.fMontant')}</Text>
      <ChoixPuces pleine value={f.montant} onChange={v => set('montant', v)}
        options={[
          { id: 'exact', label: t('finances.mExact') },
          { id: '10', label: '± 10 %' },
          { id: '35', label: '± 35 %' },
          { id: 'tous', label: t('finances.mTous') },
        ]} />
      {!!bornes(f.montant) && <Text style={s.aide}>{bornes(f.montant)}</Text>}

      <Text style={s.lbl}>{t('finances.sensRevenus')} / {t('finances.sensDepenses')}</Text>
      <ChoixPuces pleine value={f.sens || 'tous'} onChange={v => set('sens', v === 'tous' ? null : v)}
        options={[
          { id: 'depense', label: t('finances.sensDepenses') },
          { id: 'revenu', label: t('finances.sensRevenus') },
          { id: 'tous', label: t('finances.mTous') },
        ]} />

      {/* Ce qu'il RESTE a affecter sur l'operation. Distinct du filtre
          « rattachement » juste dessous : une operation peut etre rattachee
          ailleurs ET garder du reste. */}
      <Text style={s.lbl}>{t('finances.fReste')}</Text>
      <ChoixPuces pleine value={f.reste} onChange={v => set('reste', v)}
        options={[
          { id: 'nonNul', label: t('finances.resteNonNul') },
          { id: 'toutes', label: t('finances.mTous') },
        ]} />

      <Text style={s.lbl}>{t('finances.fRattachement')}</Text>
      <ChoixPuces pleine value={f.rattachement} onChange={v => set('rattachement', v)}
        options={[
          { id: 'non', label: t('finances.rNon') },
          { id: 'oui', label: t('finances.rOui') },
          { id: 'toutes', label: t('finances.rToutes') },
        ]} />

      {comptes.length > 1 && (
        <>
          <Text style={s.lbl}>{t('finances.fCompte')}</Text>
          <TouchableOpacity style={[s.item, !(f.comptes || []).length && s.itemOn]} activeOpacity={0.7}
            onPress={() => set('comptes', null)}>
            <Text style={s.coche}>{!(f.comptes || []).length ? '☑' : '☐'}</Text>
            <Text style={s.itemTxt}>{t('finances.mTous')}</Text>
          </TouchableOpacity>
          {comptes.map(c => {
            const on = (f.comptes || []).includes(c.id);
            return (
              <TouchableOpacity key={c.id} style={[s.item, on && s.itemOn]} activeOpacity={0.7}
                onPress={() => basculerCompte(c.id)}>
                <Text style={s.coche}>{on ? '☑' : '☐'}</Text>
                <Text style={{ fontSize: 16 }}>{emojiCompte(c)}</Text>
                <Text style={s.itemTxt} numberOfLines={1}>{c.nom}</Text>
              </TouchableOpacity>
            );
          })}
        </>
      )}

      <TouchableOpacity style={s.reinit} activeOpacity={0.7}
        onPress={() => { const n = { ...FILTRES_DEFAUT, sens: sensDefaut }; setF(n); onAppliquer?.(n); }}>
        <Text style={s.reinitTxt}>↺ {t('finances.reinitFiltres')}</Text>
      </TouchableOpacity>
    </CalqueChoix>
  );
}

const fmt = (n) => `${(Math.round(Math.abs(n) * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;

const makeStyles = (UI) => StyleSheet.create({
  lbl: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 16, marginBottom: 6 },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 6 },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 8 },
  itemOn: { borderColor: UI.accent },
  coche: { fontSize: 16, color: UI.accent, width: 20 },
  itemTxt: { flex: 1, fontSize: 14.5, color: UI.ink },
  reinit: { alignItems: 'center', paddingVertical: 12, marginTop: 18, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border },
  reinitTxt: { fontSize: 13.5, fontWeight: '700', color: UI.muted },
});
